import { useFormContext, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { jt, t } from "ttag";

import { ROUTES } from "@/app/router";
import {
  useActiveSubscriptions,
  useChangeNextPeriodSubscriptionPlan,
} from "@/features/billing/hooks";
import { LogoLoader } from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import { Alert, Button, Checkbox, Form } from "@/shared/ui";
import type { SubscriptionFormValues } from "../shared";

export const ChangeSubscriptionFooter = () => {
  const {
    data: activeSubscriptions,
    isSuccess,
    isLoading,
  } = useActiveSubscriptions();

  const { control, handleSubmit } = useFormContext<SubscriptionFormValues>();

  const { mutate, isPending } = useChangeNextPeriodSubscriptionPlan();

  const navigate = useNavigate();

  const nextSubscriptionPlanId = useWatch({
    control,
    name: "productPriceId",
  });

  const wlActiveSubscription = activeSubscriptions?.find(
    (subscription) =>
      subscription.currentProduct.category.slug === CATEGORY_SLUG.WEIGHT_LOSS,
  );

  const submitDisabled = !nextSubscriptionPlanId || isLoading;

  const handleChangeSubscription = handleSubmit((values) => {
    if (isSuccess && wlActiveSubscription) {
      mutate(
        {
          subscriptionId: wlActiveSubscription.id,
          productPriceId: values.productPriceId,
        },
        {
          onSuccess: () => navigate(ROUTES.BILLING.OVERVIEW),
        },
      );
    }
  });

  const takeEffectStrong = (
    <strong key="take-effect-strong">{t`will take effect starting from your next billing period`}</strong>
  );
  const noCommitmentStrong = (
    <strong key="no-commitment-strong">{t`No commitment - cancel anytime!`}</strong>
  );

  return (
    <div className="flex flex-col items-center gap-1 pb-6 md:pb-12">
      <div className="flex flex-col items-center gap-4">
        <Alert type="warning">
          {jt`Any changes to your subscription plan ${takeEffectStrong}. This means that your current plan will remain active until the end of the current billing cycle. ${noCommitmentStrong}`}
        </Alert>

        <Form.Field
          control={control}
          name="hasConsent"
          disabled={isLoading}
          render={({ field: { value, onChange, ...rest } }) => (
            <Form.Item>
              <Form.Control>
                <Checkbox.Item
                  checked={value}
                  onCheckedChange={onChange}
                  {...rest}
                >
                  <Checkbox.Label>
                    {t`I agree to the new charge associated with my subscription and understand the changes in pricing`}
                  </Checkbox.Label>
                </Checkbox.Item>
              </Form.Control>

              <Form.Message compact={false} className="text-center" />
            </Form.Item>
          )}
        />
      </div>

      <Button
        disabled={submitDisabled}
        onClick={handleChangeSubscription}
        size="lg"
        className="w-fit"
      >
        {t`Change subscription`}
      </Button>

      <LogoLoader show={isPending} fullscreen />
    </div>
  );
};
